import React, { useEffect, useRef } from "react";

function profileHint(code) {
  if (code === "perfil_edad_anos") return "Escribe tu edad en años.";
  if (code === "perfil_altura_cm") return "Escribe tu estatura en centímetros (cm).";
  if (code === "perfil_peso_kg") return "Escribe tu peso en kilogramos (kg).";
  return "";
}

function QuestionInput({ question, answer, error, onAnswer }) {
  const errorId = `onboarding-question-${question.id}-error`;

  if (question.type === "OPEN_TEXT") {
    const hint = question.isProfile ? profileHint(question.code) : "";
    const commonProps = {
      id: `onboarding-question-${question.id}`,
      className: question.isProfile ? "onboarding-input" : "onboarding-textarea",
      value: typeof answer === "string" ? answer : "",
      onChange: (event) => onAnswer(event.target.value),
      "aria-invalid": Boolean(error),
      "aria-describedby": error ? errorId : hint ? `${errorId}-hint` : undefined,
      required: question.required,
    };

    return (
      <div className="onboarding-field">
        <label htmlFor={commonProps.id}>
          {question.prompt}
          {question.required ? " *" : ""}
        </label>
        {question.isProfile ? (
          <input {...commonProps} type="text" inputMode="numeric" pattern="[0-9]*" />
        ) : (
          <textarea {...commonProps} rows={4} />
        )}
        {hint && <p id={`${errorId}-hint`} className="onboarding-help">{hint}</p>}
        {error && <p id={errorId} className="onboarding-error" role="alert">{error}</p>}
      </div>
    );
  }

  if (question.type === "SINGLE_SELECTION") {
    const selected = answer === undefined || answer === null ? "" : String(answer);
    return (
      <fieldset
        className="onboarding-form"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
      >
        <legend className="onboarding-field-label">
          {question.prompt}{question.required ? " *" : ""}
        </legend>
        <div className="onboarding-option-list">
          {question.options.map((option) => {
            const id = `onboarding-question-${question.id}-option-${option.id}`;
            return (
              <label className="onboarding-radio" htmlFor={id} key={option.id}>
                <input
                  id={id}
                  type="radio"
                  name={`onboarding-question-${question.id}`}
                  value={option.id}
                  checked={selected === String(option.id)}
                  onChange={() => onAnswer(String(option.id))}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
        {error && <p id={errorId} className="onboarding-error" role="alert">{error}</p>}
      </fieldset>
    );
  }

  const selectedOptions = Array.isArray(answer) ? answer.map(String) : [];
  return (
    <fieldset
      className="onboarding-form"
      aria-invalid={Boolean(error)}
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="onboarding-field-label">
        {question.prompt}{question.required ? " *" : ""}
      </legend>
      <div className="onboarding-option-list">
        {question.options.map((option) => {
          const id = `onboarding-question-${question.id}-option-${option.id}`;
          const checked = selectedOptions.includes(String(option.id));
          return (
            <label className="onboarding-check-option" htmlFor={id} key={option.id}>
              <input
                id={id}
                type="checkbox"
                name={`onboarding-question-${question.id}`}
                value={option.id}
                checked={checked}
                onChange={() => {
                  const next = checked
                    ? selectedOptions.filter((selectedId) => selectedId !== String(option.id))
                    : [...selectedOptions, String(option.id)];
                  onAnswer(next);
                }}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
      {error && <p id={errorId} className="onboarding-error" role="alert">{error}</p>}
    </fieldset>
  );
}

export default function SurveyStep({
  questions,
  questionIndex,
  answers,
  fieldErrors,
  onAnswer,
  onContinue,
  onBack,
  error,
}) {
  const question = questions[questionIndex];
  const headingRef = useRef(null);

  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [questionIndex]);

  if (!question) return null;
  const progress = Math.round(((questionIndex + 1) / questions.length) * 100);

  return (
    <div className="onboarding-card-inner">
      <div className="onboarding-step-head">
        <div>
          <h2 ref={headingRef} tabIndex="-1">Ficha de salud</h2>
          <p>Responde las preguntas que aparecen en tu ficha.</p>
        </div>
        <span className="onboarding-step-count" aria-live="polite">
          Pregunta {questionIndex + 1} de {questions.length}
        </span>
      </div>

      <div
        className="onboarding-progress"
        role="progressbar"
        aria-label="Progreso de la ficha"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={progress}
      >
        <span style={{ width: `${progress}%` }} />
      </div>

      {error && <p className="onboarding-error" role="alert">{error}</p>}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onContinue();
        }}
      >
        <QuestionInput
          question={question}
          answer={answers[question.id]}
          error={fieldErrors[question.id]}
          onAnswer={(answer) => onAnswer(question.id, answer)}
        />
        <div className="onboarding-button-row">
          <button
            className="onboarding-button onboarding-button--secondary"
            type="button"
            onClick={onBack}
            disabled={questionIndex === 0}
          >
            Anterior
          </button>
          <button className="onboarding-button onboarding-button--primary" type="submit">
            {questionIndex === questions.length - 1 ? "Revisar ficha" : "Siguiente"}
          </button>
        </div>
      </form>
    </div>
  );
}
